/**
 * The legacy Verlauf's five time buckets (ADR-034 D4 — `DashboardNews` L2565-2598): today (since local midnight),
 * this week (since Monday 00:00 local), last week (the Monday before), the last three months (since the 1st of the
 * month three months back), older. Computed against a caller-supplied `now` so the boundaries are testable.
 */
export type NewsBucket = "today" | "thisWeek" | "lastWeek" | "lastThreeMonths" | "older";
export const NEWS_BUCKETS: readonly NewsBucket[] = ["today", "thisWeek", "lastWeek", "lastThreeMonths", "older"];
/** Label KEYS per bucket — this module is pure date logic with no translator of its own. */
export const NEWS_BUCKET_LABEL_KEY = {
  today: "dashboard.timeCategories.today",
  thisWeek: "dashboard.timeCategories.thisWeek",
  lastWeek: "dashboard.timeCategories.lastWeek",
  lastThreeMonths: "dashboard.timeCategories.lastThreeMonths",
  older: "dashboard.timeCategories.moreThanThreeMonthsAgo",
} as const satisfies Record<NewsBucket, string>;

export function newsBucket(createdAt: string | Date, now: Date): NewsBucket {
  const t = new Date(createdAt).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (t >= today.getTime()) return "today";
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7)); // JS Sunday = 0 → Monday-based week
  if (t >= monday.getTime()) return "thisWeek";
  const lastMonday = new Date(monday);
  lastMonday.setDate(monday.getDate() - 7);
  if (t >= lastMonday.getTime()) return "lastWeek";
  const threeMonths = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  if (t >= threeMonths.getTime()) return "lastThreeMonths";
  return "older";
}

/** Group items (already newest-first) into the buckets, keeping the order; empty buckets are omitted (the legacy hid them). */
export function bucketNews<T extends { createdAt: string }>(items: readonly T[], now: Date): Array<{ bucket: NewsBucket; items: T[] }> {
  const map = new Map<NewsBucket, T[]>();
  for (const it of items) {
    const b = newsBucket(it.createdAt, now);
    const list = map.get(b) ?? [];
    list.push(it);
    map.set(b, list);
  }
  return NEWS_BUCKETS.flatMap((b) => (map.has(b) ? [{ bucket: b, items: map.get(b)! }] : []));
}
